import jwtDecode from "jwt-decode";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  FormControl,
  FormHelperText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import * as yup from "yup";
import history from "@history";
import InnerFooter from "app/shared-components/InnerFooter";
import { DatePicker } from "@mui/x-date-pickers";
import { useDispatch } from "react-redux";
import { showMessage } from "app/store/fuse/messageSlice";

//#region form declaration
const schema = yup.object().shape({
  emailAddress: yup.string(),
  firstName: yup.string(),
  lastName: yup.string(),
  dob: yup.string(),
  language: yup.string(),
  gender: yup.string(),
  ssn: yup.string(),
  relationship: yup.string(),
  password: yup.string(),
  confirmPassword: yup.string(),
});

const defaultValues = {
  emailAddress: "",
  firstName: "",
  lastName: "",
  dob: "",
  language: "",
  gender: "",
  ssn: "",
  relationship: "",
  password: "",
  confirmPassword: "",
};
//#endregion

const ParentSignUp = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(location.search);
  const [youthName, setYouthName] = useState<string>("");

  useEffect(() => {
    const token = searchParams.get("_tk");
    decodeToken(token ?? undefined);
  }, []);

  interface DecodedToken {
    parentEmail: string;
    [key: string]: any;
  }

  const languageOptions = [
    { code: "en", label: "English - US" },
    { code: "am", label: "Amharic (አማርኛ)" },
    { code: "zh", label: "Chinese (中文)" },
    { code: "fr", label: "French (Français)" },
    { code: "ko", label: "Korean (한국어)" },
    { code: "es", label: "Spanish (Español)" },
    { code: "vi", label: "Vietnamese (Tiếng Việt)" },
  ];

  const { control, formState, handleSubmit, setValue } = useForm({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(schema),
  });

  const decodeToken = async (token?: string) => {
    if (token) {
      const { parentEmail, youthFullName } = jwtDecode<DecodedToken>(token);
      setYouthName(youthFullName);
      setValue("emailAddress", parentEmail);
    }
  };

  const { errors } = formState;

  function onSubmit(data: any): void {
    dispatch(
      showMessage({
        message: "Parent Registrations Successfully.",
        variant: "success",
      })
    );
    history.push(`/sign-in`);
  }

  const [formValues, setFormValues] = useState({
    firstName: "",
    dob: "",
    language: "",
    gender: "",
    relationship: "",
  });

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  return (
    <div className="flex flex-col sm:flex-row items-center sm:justify-center flex-1 min-w-0">
      <Paper className="h-full sm:h-auto w-full sm:w-auto py-8 px-16 sm:p-48 sm:rounded-2xl sm:shadow md:shadow-none ltr:border-r-1 rtl:border-l-1">
        <div className="w-full sm:w-640 mx-auto sm:mx-0">
          <div className="flex items-start justify-between">
            <div>
              <img
                className="h-52"
                src="assets/images/logo/logo-ypms-full.png"
                alt="logo"
              />

              <Typography className="mt-16 text-2xl font-semibold tracking-tight leading-tight">
                Register with us
              </Typography>

              <Typography className="m-16 ml-0 font-semibold">
                to continue, enter Parent Information
              </Typography>
            </div>
            <div className="flex justify-between content-end">
              <img
                className="h-128"
                src="assets/images/cliparts/information.png"
                alt="logo"
              />
            </div>
          </div>

          <form
            name="hostInfoForm"
            noValidate
            className="flex flex-col justify-center w-full mt-16"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex items-start justify-center gap-16">
              <Controller
                name="emailAddress"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email Address"
                    size="small"
                    error={!!errors.emailAddress}
                    helperText={errors?.emailAddress?.message}
                    variant="outlined"
                    className="w-full"
                    disabled
                  />
                )}
              />
            </div>

            <div className="flex items-start justify-center gap-16 mt-16">
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    size="small"
                    error={!!errors.firstName}
                    helperText={errors?.firstName?.message}
                    variant="outlined"
                    className="w-1/2"
                  />
                )}
              />

              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    size="small"
                    error={!!errors.lastName}
                    helperText={errors?.lastName?.message}
                    variant="outlined"
                    className="w-1/2"
                  />
                )}
              />
            </div>

            <div className="flex items-start justify-center gap-16 mt-16">
              <Controller
                name="dob"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    className="w-1/2"
                    label="Date of Birth"
                    slotProps={{ textField: { size: "small" } }}
                  />
                )}
              />
              <Controller
                name="language"
                control={control}
                render={({ field }) => (
                  <FormControl error={!!errors.language} className="w-1/2">
                    <Select
                      {...field}
                      value={formValues.language}
                      onChange={handleChange}
                      displayEmpty
                      size="small"
                    >
                      <MenuItem value="">
                        <em>Language</em>
                      </MenuItem>
                      {languageOptions.map(({ code, label }) => (
                        <MenuItem key={code} value={code}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{errors?.language?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </div>

            <div className="flex items-start justify-start gap-16 mt-16">
              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <FormControl error={!!errors.gender} className="w-1/2">
                    <Select
                      {...field}
                      value={formValues.gender}
                      onChange={handleChange}
                      displayEmpty
                      size="small"
                    >
                      <MenuItem value="">
                        <em>Gender</em>
                      </MenuItem>
                      <MenuItem value={1}>Male</MenuItem>
                      <MenuItem value={2}>Female</MenuItem>
                      <MenuItem value={3}>Other</MenuItem>
                    </Select>
                    <FormHelperText>{errors?.gender?.message}</FormHelperText>
                  </FormControl>
                )}
              />
              <Controller
                name="ssn"
                control={control}
                render={({ field }) => (
                  <FormControl className="w-1/2 pr-5">
                    <TextField
                      {...field}
                      label="Social Security Number"
                      size="small"
                      error={!!errors.ssn}
                      helperText={errors?.ssn?.message}
                      variant="outlined"
                    />
                  </FormControl>
                )}
              />
              <Controller
                name="relationship"
                control={control}
                render={({ field }) => (
                  <FormControl error={!!errors.relationship} className="w-1/2">
                    <Select
                      {...field}
                      value={formValues.relationship}
                      onChange={handleChange}
                      displayEmpty
                      size="small"
                    >
                      <MenuItem value="">
                        <em>{`Relationship to ${youthName}`}</em>
                      </MenuItem>
                      <MenuItem value={1}>Mother</MenuItem>
                      <MenuItem value={2}>Father</MenuItem>
                      <MenuItem value={3}>Other</MenuItem>
                    </Select>
                    <FormHelperText>{errors?.gender?.message}</FormHelperText>
                  </FormControl>
                )}
              />
            </div>

            <div className="flex items-start justify-center gap-16 mt-16">
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Password"
                    size="small"
                    type="password"
                    error={!!errors.password}
                    helperText={errors?.password?.message}
                    variant="outlined"
                    className="w-1/2"
                    autoComplete="new-password"
                  />
                )}
              />

              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Confirm Password"
                    type="password"
                    size="small"
                    error={!!errors.confirmPassword}
                    helperText={errors?.confirmPassword?.message}
                    variant="outlined"
                    className="w-1/2"
                  />
                )}
              />
            </div>

            <div className="flex items-end place-content-end mb-24">
              <Button
                variant="contained"
                color="secondary"
                className="mt-24 min-w-96"
                aria-label="Register"
                type="submit"
                size="medium"
              >
                Register
              </Button>
            </div>
            <InnerFooter />
          </form>
        </div>
      </Paper>
    </div>
  );
};

export default ParentSignUp;
